/**
 * PATH: packages/kanban-host/src/_internal/notify/useToast.ts
 * FILE_ID: KANBANHOST-INTERNAL-USE-TOAST-001
 * PURPOSE: Setter-based DI seam for the tenant's `useToast` hook.
 *          Lifted kanban pages (KanbanPage, PlannerPage, InboxPage)
 *          used to import `useToast` from the tenant's local
 *          `@/shared/components/Toast`. Same shape as
 *          `_internal/auth/useApiAuth.ts` — see that file for the
 *          rationale on the setter pattern.
 * WHY:     The tenant's <ToastProvider> sits ABOVE the kanban routes
 *          in the React tree. We just need a way for kanban-host code
 *          to *call* the tenant's `useToast()` without literally
 *          importing it (because the package can't have a tenant-path
 *          import). The setter passes the tenant's `useToast` once at
 *          boot and we re-export it under our internal name.
 * RELATED:
 *   - apps/<tenant>/src/shared/components/Toast.tsx: tenant
 *     implementation (registered).
 *   - packages/email-hub/src/_internal/components/Toast.tsx:
 *     differs in approach — email-hub embeds its OWN ToastProvider
 *     because it can run as a standalone app. Kanban-host runs only
 *     inside the tenant shell, so we can re-use the tenant's existing
 *     ToastProvider via DI.
 */

export type KanbanToastType = 'success' | 'error' | 'warning' | 'info'

export interface KanbanToastOptions {
  persist?: boolean
  source?: string
  title?: string
  /** Inline action button (e.g. "Undo" after archive — UAT P1-b).
   *  Rendered by the tenant toaster; clicking runs the callback and
   *  dismisses the toast. */
  action?: { label: string; onClick: () => void }
}

export interface KanbanToastApi {
  toast: (
    message: string,
    type?: KanbanToastType,
    options?: KanbanToastOptions,
  ) => void
}

export type UseKanbanToastHook = () => KanbanToastApi

let registered: UseKanbanToastHook | null = null

/**
 * Tenant boot hook registration. Call ONCE near the top of the app
 * entry (before the first kanban-page render).
 */
export function setKanbanUseToast(hook: UseKanbanToastHook): void {
  registered = hook
}

/**
 * Hub-internal hook consumed by lifted kanban pages. Delegates to the
 * tenant-registered hook. Falls back to a no-op toast in non-browser
 * (SSR / test) contexts when the host hasn't registered — useful so
 * unit tests of pure logic in lifted pages don't have to spin up the
 * full tenant boot just to read a value.
 */
export function useToast(): KanbanToastApi {
  if (!registered) {
    // Non-throwing fallback: in test/SSR contexts the lifted page
    // may render without the tenant boot. Logging a no-op toast
    // keeps tests green; production tenants always register.
    if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
      // eslint-disable-next-line no-console
      console.warn(
        '[@quotech/kanban-host] useToast() called before ' +
          'setKanbanUseToast() registration. Tenant boot should call ' +
          'setKanbanUseToast(useToast) before any kanban route ' +
          'renders. Falling back to no-op toaster.',
      )
    }
    return { toast: () => {} }
  }
  return registered()
}
